import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import { McpCallable } from "@beyondrealityapp/core/mcp/constants";
import type {
  McpAuthorizationPayload,
  McpAuthorizationRequest,
  McpConnection,
  McpRedirectResult,
  McpRevokeConnectionPayload,
} from "@beyondrealityapp/core/mcp/types";

const call = async <Request, Response>(name: string, data?: Request) => {
  const callable = httpsCallable<Request | undefined, Response>(functions, name);
  return (await callable(data)).data;
};

export const mcpApi = createApi({
  reducerPath: "mcpApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["McpConnection"],
  endpoints: (builder) => ({
    fetchMcpAuthorizationRequest: builder.query<McpAuthorizationRequest, string>({
      queryFn: async (requestId) => {
        try {
          return {
            data: await call<McpAuthorizationPayload, McpAuthorizationRequest>(
              McpCallable.GET_AUTHORIZATION_REQUEST,
              { requestId }
            ),
          };
        } catch (error) {
          return { error: error as Error };
        }
      },
    }),
    approveMcpAuthorization: builder.mutation<McpRedirectResult, string>({
      queryFn: async (requestId) => {
        try {
          return {
            data: await call<McpAuthorizationPayload, McpRedirectResult>(
              McpCallable.APPROVE_AUTHORIZATION,
              { requestId }
            ),
          };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["McpConnection"],
    }),
    denyMcpAuthorization: builder.mutation<McpRedirectResult, string>({
      queryFn: async (requestId) => {
        try {
          return {
            data: await call<McpAuthorizationPayload, McpRedirectResult>(
              McpCallable.DENY_AUTHORIZATION,
              { requestId }
            ),
          };
        } catch (error) {
          return { error: error as Error };
        }
      },
    }),
    fetchMcpConnections: builder.query<McpConnection[], void>({
      queryFn: async () => {
        try {
          return {
            data: await call<void, McpConnection[]>(McpCallable.LIST_CONNECTIONS),
          };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["McpConnection"],
    }),
    revokeMcpConnection: builder.mutation<{ revoked: boolean }, string>({
      queryFn: async (connectionId) => {
        try {
          return {
            data: await call<McpRevokeConnectionPayload, { revoked: boolean }>(
              McpCallable.REVOKE_CONNECTION,
              { connectionId }
            ),
          };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["McpConnection"],
    }),
  }),
});

export const {
  useFetchMcpAuthorizationRequestQuery,
  useApproveMcpAuthorizationMutation,
  useDenyMcpAuthorizationMutation,
  useFetchMcpConnectionsQuery,
  useRevokeMcpConnectionMutation,
} = mcpApi;
